import { AuthApi } from 'src/api/auth';
import { openReplitWithToken, registerReplitParentHandlers } from './openReplit';

const REDIRECT_DELAY_MS = 500;

type RedirectToReplitParams = {
  replitUrl: string;
  languagePairId?: number | null;
  existingPopup?: Window | null;
};

/**
 * Redirects to a Replit app with authentication via postMessage.
 * Opens Replit in popup and sends token via postMessage, then redirects
 * parent window to about:blank.
 *
 * Requires a user action (button click) to avoid popup blockers. Pass
 * existingPopup if you opened a popup immediately on user click.
 *
 * Note: This function is not unit tested due to JSDOM limitations
 * with window.location.href assignment. The core logic (auth and postMessage)
 * is tested via openReplitWithToken tests. This function is excluded from
 * coverage requirements.
 */
/* istanbul ignore next */
export const redirectToReplit = async ({
  replitUrl,
  languagePairId,
  existingPopup,
}: RedirectToReplitParams): Promise<void> => {
  const targetUrl = new URL(replitUrl);
  if (languagePairId) {
    targetUrl.searchParams.set('language_pair_id', String(languagePairId));
  }
  targetUrl.searchParams.set('app_origin', window.location.origin);

  let bearerToken: string;
  try {
    const { access_token } = await AuthApi.webGetAccessToken();
    bearerToken = access_token.access_token;

    if (!bearerToken || bearerToken.trim() === '') {
      throw new Error('Failed to get access token - token is empty');
    }
  } catch (error) {
    throw new Error('Failed to get access token - user may not be authenticated', {
      cause: error,
    });
  }

  // Register handler to receive messages back from Replit (same as Labs)
  registerReplitParentHandlers({
    replitOrigin: targetUrl.href,
    onWebAuth: async (token: string) => {
      await AuthApi.web({ token });
    },
  });

  // Open Replit in popup and send token via postMessage
  await openReplitWithToken({
    replitOrigin: targetUrl.href,
    token: bearerToken,
    existingPopup,
  });

  await new Promise(resolve => setTimeout(resolve, REDIRECT_DELAY_MS));
  window.location.href = 'about:blank';
};
